import {
  Autocomplete,
  Box,
  Checkbox,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { MaterialReactTable } from "material-react-table";
import moment from "moment";
import React from "react";
import { StyledEmail } from "../../styled/email";
import { tableConfig } from "./config/tableConfig";
import DetailsTableEmails from "./utilsTable/detailsTableEmails";
import TitleContainer from "./utilsTable/titleWithFetchData";

const EmailTableComponent = ({
  data,
  namesArts,
  loading,
  error,
  fetchData,
  title,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
}) => {
  const columns = [
    {
      header: "Art",
      accessorKey: "Art",
      enableColumnActions: false,
      enableFiltering: true,
      size: 100,
      muiTableBodyCellProps: {
        sx: {
          fontSize: 12,
        },
      },
      maxSize: 100,
      Cell: ({ row }) => row.original.art.toUpperCase(),
      Filter: ({ column }) => (
        <Autocomplete
          multiple
          options={Object.values(namesArts ?? [])}
          value={column.getFilterValue() || []}
          onChange={(event, newValue) =>
            column.setFilterValue(newValue.length ? newValue : undefined)
          }
          getOptionLabel={(option) => option.nombreCorto}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 35,
                },
                "& .MuiInputAdornment-root": {
                  height: 30,
                },
                "& .MuiOutlinedInput-input ": {
                  padding: "5px 10px",
                  fontSize: "0.875rem",
                },
                "& .MuiChip-root": {
                  height: 20,
                  fontSize: "0.75rem",
                  margin: "2px",
                },
                "& .MuiAutocomplete-input": {
                  padding: "5px 10px",
                  fontSize: "0.875rem",
                },
                "& .MuiAutocomplete-popover": {
                  maxHeight: 250,
                  overflowY: "auto",
                },
              }}
            />
          )}
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => (
            <ListItem {...props} key={option.id}>
              <Checkbox
                size="sm"
                checked={selected}
                sx={{
                  padding: 0,
                  margin: 0,
                  marginRight: 0.5,
                  marginLeft: -1,
                }}
              />
              <Typography variant="body2">{option.nombreCorto}</Typography>
            </ListItem>
          )}
        />
      ),
      filterFn: (row, id, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true;
        const rowValue = row.original.mlPointCoreArtId;
        return filterValue.some((value) => rowValue === value.id);
      },
    },
    {
      header: "Fecha",
      accessorKey: "fechaHoraEnvio",
      Filter: ({ column }) => {
        const [selectedDate, setSelectedDate] = React.useState(null);

        return (
          <DatePicker
            value={selectedDate}
            onChange={(newValue) => {
              setSelectedDate(newValue);
              column.setFilterValue(
                newValue ? moment(newValue).format("YYYY-MM-DD") : undefined,
              );
            }}
            slotProps={{
              textField: {
                variant: "standard",
                sx: {
                  "& .MuiInputBase-root": {
                    height: 35,
                    minWidth: 80,
                  },
                  "& .MuiOutlinedInput-input ": {
                    height: 5,
                  },
                  "& .MuiChip-root": {
                    height: 1,
                  },
                },
              },
            }}
            renderInput={(params) => <TextField fullWidth {...params} />}
          />
        );
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true; // Mostrar todas las filas si no hay filtro activo
        const cellValue = row.getValue(columnId); // Obtener el valor de la celda
        if (!cellValue) return false; // Excluir filas sin fecha si hay filtro
        return moment(cellValue).isSame(filterValue, "day"); // Verificar coincidencia exacta
      },
      Cell: ({ row }) =>
        moment(row.original.fechaHoraEnvio).isValid()
          ? moment(row.original.fechaHoraEnvio).format("DD/MM/YYYY")
          : "",
    },
    {
      header: "Siniestro",
      enableColumnActions: false,
      accessorKey: "siniestro",
      Cell: ({ row }) =>
        row.original.siniestro === 0 ? " " : row.original.siniestro,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: 35,
              minHeight: "unset",
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Documento",
      accessorKey: "numeroDocumento",
      enableColumnActions: false,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: 35,
              minHeight: "unset",
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Destinatario",
      accessorKey: "destinatario",
      Cell: ({ row }) => row.original.destinatario,
      enableColumnActions: false,
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: 35,
              minHeight: "unset",
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Asunto",
      accessorKey: "asunto",
      enableSorting: false,
      enableColumnActions: false,
      filterFn: (row, columnId, filterValue) =>
        row
          .getValue(columnId)
          .toLowerCase()
          .includes(filterValue.toLowerCase()),
      Cell: ({ row }) => {
        const asunto = row.original.asunto;
        return asunto.length > 50 ? (
          <Tooltip title={asunto} TransitionComponent={Zoom} arrow>
            <p>
              {asunto.substring(0, 50)}
              <b>
                <i>...[ver más]</i>
              </b>
            </p>
          </Tooltip>
        ) : (
          <p>{asunto}</p>
        );
      },
      Filter: ({ column }) => (
        <TextField
          variant="standard"
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: 35,
              minHeight: "unset",
            },
            "& .MuiOutlinedInput-input": {
              height: 5,
            },
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value)}
        />
      ),
    },
    {
      header: "Tiene Adjunto",
      accessorKey: "parsedHasAdjunto",
      Filter: ({ column }) => (
        <Select
          fullWidth
          variant="standard"
          sx={{
            minWidth: 50,
            minHeight: 35,
            textAlign: "start",
          }}
          value={column.getFilterValue() || ""}
          onChange={(e) => column.setFilterValue(e.target.value || undefined)}
          displayEmpty>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="true">Sí</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      ),
      filterFn: (row, id, filterValue) => {
        const value = row.getValue(id);
        return filterValue === "true" ? value === true : value === false;
      },
      Cell: ({ cell }) => (cell.getValue() ? "Sí" : "No"),
    },
  ];

  return (
    <MaterialReactTable
      {...tableConfig}
      columns={columns}
      defaultColumn={{
        maxSize: 50,
        grow: false,
      }}
      data={data}
      state={{ isLoading: loading }}
      renderTopToolbarCustomActions={() => (
        <Box m={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <TitleContainer
              title={title}
              fetchData={() => {
                fetchData(dateFrom, dateTo);
              }}
            />
          </Box>

          <div>
            <DatePicker
              variant="inline"
              format="dd/MM/yyyy"
              margin="normal"
              id="dateFrom"
              label="Desde"
              value={dateFrom}
              maxDate={dateTo}
              slotProps={{ textField: { variant: "standard" } }}
              onChange={(e) => {
                const from = moment(e).format("YYYY-MM-DDT00:00:00");
                setDateFrom(from);
                fetchData(from, dateTo);
              }}
              sx={StyledEmail.datePicker}
            />
            <DatePicker
              variant="inline"
              format="dd/MM/yyyy"
              margin="normal"
              id="dateTo"
              label="Hasta"
              slotProps={{ textField: { variant: "standard" } }}
              value={dateTo}
              maxDate={Date()}
              minDate={dateFrom}
              onChange={(e) => {
                const to = moment(e).format("YYYY-MM-DDT00:00:00");
                setDateTo(to);
                fetchData(dateFrom, to);
              }}
              sx={StyledEmail.datePicker}
            />
          </div>
        </Box>
      )}
      renderDetailPanel={({ row }) => {
        return (
          <StyledEmail.DetailsTableEmailsContainer>
            <DetailsTableEmails rowData={row.original} />
          </StyledEmail.DetailsTableEmailsContainer>
        );
      }}
    />
  );
};

export default EmailTableComponent;
